/**
 * Billing offers — the decisions the purchase screens make, kept out of the
 * screens.
 *
 * The company portal sells seats and packs, the Base extension points people
 * at them when they run out, and both have to agree on who may buy what and
 * which product to put first. Written once here, against the API's own types,
 * so the two apps cannot drift — and so the rules have tests, which the Vue
 * apps do not.
 *
 * Nothing here *grants* anything. The API decides; these functions only choose
 * what to show and read what the API answered.
 */

import type { SeatCounts, SeatTier } from '../entities'
import type {
  BillingSeatGrant,
  BillingSeatsResponse,
  BillingSummaryResponse,
  CallerAccess,
  UsageStatus,
} from '../api'
import { SEAT_TIERS } from './entitlements'

// ---------------------------------------------------------------------------
// Quota refusal → offer
// ---------------------------------------------------------------------------

/**
 * The parts of a `QuotaExceededErrorResponse` an offer depends on.
 *
 * Both flags default to false when the body lacks them: a refusal that does not
 * say a product is on sale must not be answered with a buy button that leads to
 * a page selling nothing.
 */
export interface QuotaRefusal {
  canBuyPacks: boolean
  canBuySeats: boolean
}

/**
 * Read a quota refusal off an error thrown by a frontend API client, or null
 * when the error is something else.
 *
 * The clients attach the parsed response body as `body` and its `code` on the
 * error itself; the fields are read from the body when present, else from the
 * error, so either attachment style works.
 */
export function quotaRefusalFrom(err: unknown): QuotaRefusal | null {
  if (!err || typeof err !== 'object') return null
  const e = err as { code?: unknown; body?: unknown }
  const body = (e.body && typeof e.body === 'object' ? e.body : e) as {
    code?: unknown
    can_buy_packs?: unknown
    can_buy_seats?: unknown
  }
  if (e.code !== 'QUOTA_EXCEEDED' && body.code !== 'QUOTA_EXCEEDED') return null
  return { canBuyPacks: body.can_buy_packs === true, canBuySeats: body.can_buy_seats === true }
}

/**
 * What can be offered to someone the quota gate refused.
 *
 * - `pack` / `seat`: `'buy'` sends an app_admin to the Billing page; `'assign'`
 *   sends one to the Users page, for a company that holds seats but sells no
 *   more here; `'ask_admin'` tells anyone else whom to ask, since every
 *   purchase and every assignment is app_admin-only on the server.
 * - `resetDay`: the day of the month the free allowance comes back — always
 *   true, so always offered when known.
 */
export interface QuotaOffers {
  pack: 'buy' | 'ask_admin' | null
  seat: 'buy' | 'assign' | 'ask_admin' | null
  resetDay: number | null
}

export function quotaOffers(params: {
  refusal: QuotaRefusal
  /** The caller's own access. A seated caller is never refused, so a seat is not offered to them. */
  access: Pick<CallerAccess, 'seatTier'> | null
  isAppAdmin: boolean
  /** Seats in force per tier, from `tenant.seats`. */
  seatsInForce: SeatCounts | null | undefined
  quotaResetDay: number | null | undefined
}): QuotaOffers {
  const { refusal, access, isAppAdmin, seatsInForce, quotaResetDay } = params
  const holdsSeats = SEAT_TIERS.some((tier) => (seatsInForce?.[tier] ?? 0) > 0)

  let seat: QuotaOffers['seat'] = null
  // 'buy' wins over 'assign' for an admin even when a seat might be free:
  // GET /api/tenant carries the seats in force, not how many are taken, so
  // "assign" could send them to a full Users page. Billing shows both numbers
  // and links to Users.
  if (access?.seatTier == null) {
    if (isAppAdmin) seat = refusal.canBuySeats ? 'buy' : holdsSeats ? 'assign' : null
    // A member cannot tell a full set of seats from one with room, and neither
    // can we from here: the admin they ask can, on the Users page.
    else if (refusal.canBuySeats || holdsSeats) seat = 'ask_admin'
  }

  return {
    pack: refusal.canBuyPacks ? (isAppAdmin ? 'buy' : 'ask_admin') : null,
    seat,
    resetDay: quotaResetDay ?? null,
  }
}

// ---------------------------------------------------------------------------
// Usage line
// ---------------------------------------------------------------------------

/**
 * What a person's own usage line says.
 *
 * Read per caller: a seated user in a company that has used its free
 * allowance is not out of anything, and a quota bar would tell them otherwise.
 */
export type UsageDisplay =
  | { kind: 'pro' }
  | { kind: 'seat'; tier: SeatTier }
  | { kind: 'unlimited' }
  | {
      kind: 'metered'
      used: number
      quota: number
      packBalance: number
      packExpiresAt: number | null
      resetDay: number | null
    }

export function usageDisplay(
  access: Pick<CallerAccess, 'seatTier' | 'unlimited'> | null | undefined,
  usage: Pick<UsageStatus, 'monthlyQuota' | 'generationsUsed' | 'packBalance' | 'packExpiresAt' | 'quotaResetDay'> | null | undefined,
  /**
   * Pro access is in force (`PlanStatus.hasAccess`, trial included). Pro is
   * what makes it unlimited then, so it is named even for a seated person:
   * "(Starter seat)" under Pro suggested the seat was what paid.
   */
  pro = false,
): UsageDisplay | null {
  if (pro) return { kind: 'pro' }
  if (access?.seatTier && access.unlimited) return { kind: 'seat', tier: access.seatTier }
  if (access?.unlimited) return { kind: 'unlimited' }
  if (!usage) return null
  // A stored null quota is a company an operator made unlimited.
  if (usage.monthlyQuota === null) return { kind: 'unlimited' }
  return {
    kind: 'metered',
    used: usage.generationsUsed,
    quota: usage.monthlyQuota,
    packBalance: usage.packBalance ?? 0,
    packExpiresAt: usage.packExpiresAt ?? null,
    resetDay: usage.quotaResetDay,
  }
}

// ---------------------------------------------------------------------------
// Seats on the Billing page
// ---------------------------------------------------------------------------

/** Seats are sold monthly or annually; quarterly is Pro-only. */
export type SeatPeriod = 'monthly' | 'annual'

export const SEAT_PERIODS: readonly SeatPeriod[] = ['monthly', 'annual']

/** One billing period of one seat tier, as the Billing page offers it. */
export interface SeatPeriodOption {
  period: SeatPeriod
  /** The price a new subscription is checked out with. null when it is not sold here. */
  priceId: string | null
  /** The tenant's own Paddle subscription on this period, whose quantity can be changed. */
  subscription: BillingSeatGrant | null
  /** A new subscription can be bought: on sale, none held on this period, and not billing-blocked. */
  canBuy: boolean
  /** More seats can be added to the subscription. Barred after a chargeback, and while it is ending. */
  canIncrease: boolean
  /** Seats can be removed. Allowed while blocked — reducing spend must stay open. */
  canDecrease: boolean
  /**
   * The subscription is held but its price is no longer sold here, so the API
   * refuses any change (PRODUCT_UNAVAILABLE). Shown, with no controls.
   */
  unavailable: boolean
  /**
   * More than one subscription holds seats on this period (a second checkout),
   * which the API refuses to change (SEAT_SUBSCRIPTION_AMBIGUOUS). Shown, with
   * no controls; changed in the billing portal.
   */
  ambiguous: boolean
}

export interface SeatTierOptions {
  tier: SeatTier
  /** Periods to render, in the order of SEAT_PERIODS. Empty means the tier is not shown at all. */
  periods: SeatPeriodOption[]
  /** Seats an operator issued: shown, never changeable from the portal. */
  fixedGrants: BillingSeatGrant[]
  /**
   * Paddle subscriptions beyond the one listed per period — a second checkout
   * for a tier and period already held. Paid for, so never labelled as an
   * operator's; the API cannot tell which one to change, so they are changed in
   * the billing portal.
   */
  extraSubscriptions: BillingSeatGrant[]
}

/**
 * What the Billing page shows for one seat tier.
 *
 * A period is listed when it is on sale **or** the tenant already holds a
 * subscription on it — a product that stops being sold must not hide seats a
 * customer is still paying for. A period that is neither is left out rather
 * than shown disabled: a button nobody can press is a dead end.
 *
 * Buying on a period the tenant already holds would open a second subscription
 * for the same thing; the API turns it into a quantity change instead, so the
 * page offers the quantity change.
 */
export function seatTierOptions(summary: BillingSummaryResponse, tier: SeatTier): SeatTierOptions {
  const blocked = summary.billingBlocked
  const grants = summary.seats[tier]?.grants ?? []
  const adjustable = grants.filter((g) => g.adjustable)
  const periods: SeatPeriodOption[] = []

  for (const period of SEAT_PERIODS) {
    const offer = summary.products.seats[tier]?.[period]
    const priceId = offer?.available && offer.priceId ? offer.priceId : null
    const held = adjustable.filter((g) => g.billingPeriod === period)
    // The one that renews, when one of two is ending: it is the one a customer
    // thinks of as "my seats".
    const subscription = held.find((g) => g.scheduledCancelAt == null) ?? held[0] ?? null
    if (!priceId && !subscription) continue
    const ending = subscription?.scheduledCancelAt != null
    // Two subscriptions on one period: the API cannot tell which to change and
    // refuses with SEAT_SUBSCRIPTION_AMBIGUOUS, so no control may lead there.
    const ambiguous = held.length > 1
    // The API PATCHes with this environment's price for the period; with none
    // it refuses rather than move the customer to another product.
    const changeable = !!subscription && !ending && !!priceId && !ambiguous
    periods.push({
      period,
      priceId,
      subscription,
      canBuy: !subscription && !!priceId && !blocked,
      canIncrease: changeable && !blocked,
      canDecrease: changeable && (subscription?.quantity ?? 0) > 1,
      unavailable: !!subscription && !priceId,
      ambiguous,
    })
  }

  const listed = new Set(periods.map((p) => p.subscription?.id).filter(Boolean))
  const unlisted = grants.filter((g) => !listed.has(g.id))
  return {
    tier,
    periods,
    fixedGrants: unlisted.filter((g) => !g.adjustable),
    extraSubscriptions: unlisted.filter((g) => g.adjustable),
  }
}

/**
 * Seats on the tenant's changeable subscription for one tier and period, or
 * null when there is none — what a purchase or change was decided against.
 *
 * POST /api/tenant/billing/seats takes a new *total* and changes an existing
 * subscription on the spot, with no overlay. A page that went stale (seats
 * bought or changed in another tab) would otherwise send a total chosen against
 * old numbers — "buy 1" landing on a subscription of 2 is a reduction nobody
 * confirmed. The portal re-reads the summary and compares this value with the
 * one the person saw before it sends anything.
 */
export function heldSeatQuantity(summary: BillingSummaryResponse, tier: SeatTier, period: SeatPeriod): number | null {
  return seatTierOptions(summary, tier).periods.find((p) => p.period === period)?.subscription?.quantity ?? null
}

/**
 * Why POST /api/tenant/billing/seats refused, as something a screen can word.
 *
 * - `ambiguous`: more than one subscription holds seats on the period named (a second checkout)
 * - `ending`: the subscription is scheduled to cancel — resume it in the portal
 * - `unavailable`: the environment no longer sells that subscription's price
 * - `blocked`: purchases are barred after a chargeback
 * - `failed`: anything else; the generic retry message
 */
export type SeatChangeRefusal = 'ambiguous' | 'ending' | 'unavailable' | 'blocked' | 'failed'

export function seatChangeRefusal(err: unknown): SeatChangeRefusal {
  const code = err && typeof err === 'object' ? (err as { code?: unknown }).code : undefined
  switch (code) {
    case 'SEAT_SUBSCRIPTION_AMBIGUOUS':
      return 'ambiguous'
    case 'SEAT_SUBSCRIPTION_ENDING':
      return 'ending'
    case 'PRODUCT_UNAVAILABLE':
      return 'unavailable'
    case 'BILLING_BLOCKED':
      return 'blocked'
    default:
      return 'failed'
  }
}

/**
 * What a successful answer from POST /api/tenant/billing/seats asks the page to
 * do next: open a checkout (no subscription for that tier and period yet), or
 * wait for the webhook to write the changed quantity.
 *
 * `needsCheckout` is read strictly: the API always sends it, and treating a
 * missing one as "changed" would claim a purchase that never happened.
 */
export function seatChangeNextStep(res: BillingSeatsResponse): 'checkout' | 'changed' | 'failed' {
  if (res.needsCheckout === true) return 'checkout'
  if (res.needsCheckout === false && res.success === true) return 'changed'
  return 'failed'
}

// ---------------------------------------------------------------------------
// After a checkout
// ---------------------------------------------------------------------------

/** Which purchase is being waited for. */
export type PurchaseKind = 'pro' | 'templates' | 'seats' | 'pack'

/** The numbers a purchase moves, read from GET /api/tenant before and after. */
export interface BillingSnapshot {
  trial: boolean
  expiresAt: number | null
  extraTemplates: number
  seats: SeatCounts
  packBalance: number
}

/**
 * Whether the webhook has applied a purchase yet.
 *
 * The portal polls after a checkout and stops as soon as this is true, so it
 * must be something the purchase changes and nothing else does:
 *
 * - `pro`: not "isPro became true" — a trial already counts as Pro, and
 *   trial-to-paid is the main path. Paid Pro replacing a trial drops the trial
 *   flag and moves the expiry.
 * - `seats`: a seat count in either direction, because a quantity change can
 *   lower it as well as raise it.
 * - `pack`: the balance rising. A document drawn during the wait lowers it by
 *   one; a pack is 1,000 at least, so the rise still shows.
 */
export function purchaseLanded(kind: PurchaseKind, before: BillingSnapshot, after: BillingSnapshot): boolean {
  switch (kind) {
    case 'pro':
      return (before.trial && !after.trial) || after.expiresAt !== before.expiresAt
    case 'templates':
      return after.extraTemplates > before.extraTemplates
    case 'seats':
      return SEAT_TIERS.some((tier) => after.seats[tier] !== before.seats[tier])
    case 'pack':
      return after.packBalance > before.packBalance
  }
}
